export type AuthRuntimeContext = 'native-app-view' | 'mobile-browser' | 'desktop-browser';

export interface AuthRuntimeWindow {
  innerWidth: number;
  navigator: {
    userAgent?: string;
    maxTouchPoints?: number;
    standalone?: boolean;
  };
  matchMedia?: (query: string) => { matches: boolean };
  ontouchstart?: unknown;
  SignInHandler?: { postMessage?: (message: string) => void };
  SignUpHandler?: { postMessage?: (message: string) => void };
  LogOutHandler?: { postMessage?: (message: string) => void };
  ScanScreen?: { postMessage?: (message: string) => void };
}

export function hasNativeLoginBridge(win: AuthRuntimeWindow): boolean {
  return !!(win.SignInHandler?.postMessage || win.SignUpHandler?.postMessage);
}

export function hasNativeScanBridge(win: AuthRuntimeWindow): boolean {
  return !!win.ScanScreen?.postMessage;
}

function isStandalonePwa(win: AuthRuntimeWindow): boolean {
  if (win.navigator?.standalone === true) {
    return true;
  }
  if (typeof win.matchMedia !== 'function') {
    return false;
  }

  return ['standalone', 'fullscreen', 'minimal-ui'].some((mode) => {
    try {
      return win.matchMedia!(`(display-mode: ${mode})`).matches;
    } catch {
      return false;
    }
  });
}

export function detectAuthRuntimeContext(win: AuthRuntimeWindow): AuthRuntimeContext {
  // The native bridge is authoritative. A Flutter WebView also has a mobile user-agent,
  // so this check must happen before any phone/browser heuristics.
  if (hasNativeLoginBridge(win)) {
    return 'native-app-view';
  }

  const isMobileUserAgent = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(win.navigator?.userAgent || '');
  const isSmallScreen = win.innerWidth <= 768;
  const isTouchScreen = (win.navigator?.maxTouchPoints || 0) > 0 || 'ontouchstart' in win;
  const isMobileBrowser = isStandalonePwa(win)
    || isMobileUserAgent
    || (isSmallScreen && isTouchScreen);

  return isMobileBrowser ? 'mobile-browser' : 'desktop-browser';
}
